import React, { useRef, useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { User, Edit, Loader2 } from 'lucide-react';

interface SimpleAvatarUploadProps {
  avatarUrl: string | null;
  avatarPreview: string | null;
  userId: string;
  onAvatarChange: (file: File | null, preview: string | null) => void;
  isUploading?: boolean;
  fallbackText?: string;
}

const SimpleAvatarUpload: React.FC<SimpleAvatarUploadProps> = ({ 
  avatarUrl, 
  avatarPreview,
  userId,
  onAvatarChange,
  isUploading = false,
  fallbackText = 'U'
}) => {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewError, setPreviewError] = useState<boolean>(false);
  
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) {
      return;
    }
    
    const file = event.target.files[0];
    
    // Reset preview error state
    setPreviewError(false);
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid file type",
        description: "Please upload an image file (PNG, JPEG, GIF, etc.)",
        variant: "destructive",
        duration: 8000,
      });
      
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }
    
    // Validate file size
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Avatar image should be less than 5MB",
        variant: "destructive",
        duration: 8000,
      });
      
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }
    
    if (file.size === 0) {
      toast({
        title: "Invalid file",
        description: "The selected file appears to be empty",
        variant: "destructive",
        duration: 8000,
      });
      
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }
    
    // Direct upload without cropping - simple circular display
    const previewUrl = URL.createObjectURL(file);
    onAvatarChange(file, previewUrl);
    
    // Clear the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Determine which image to display with cache busting
  const displayUrl = avatarPreview || (avatarUrl ? `${avatarUrl}?t=${Date.now()}` : null);

  return (
    <div className="relative">
      <Avatar 
        className="h-24 w-24"
        style={{ 
          borderRadius: '50%',
          overflow: 'hidden'
        }}
      >
        {displayUrl && !previewError ? (
          <AvatarImage 
            src={displayUrl} 
            alt="Profile"
            crossOrigin="anonymous" 
            onError={(e) => { 
              console.error("Avatar image failed to load:", displayUrl); 
              e.currentTarget.style.display = 'none';
              setPreviewError(true);
            }}
            style={{ 
              borderRadius: '50%',
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              clipPath: 'circle(50% at 50% 50%)'
            }}
          />
        ) : (
          <AvatarFallback 
            className="bg-amber-100 text-amber-800"
            style={{ 
              borderRadius: '50%',
              width: '100%',
              height: '100%'
            }}
          >
            {fallbackText || <User size={32} />}
          </AvatarFallback>
        )}
      </Avatar>
      {isUploading ? (
        <div className="absolute -bottom-2 -right-2 bg-amber-600 text-white p-1.5 rounded-full">
          <Loader2 size={16} className="animate-spin" />
        </div>
      ) : (
        <label 
          htmlFor={`avatar-upload-${userId}`}
          className="absolute -bottom-2 -right-2 bg-amber-600 hover:bg-amber-700 text-white p-1.5 rounded-full cursor-pointer"
        >
          <Edit size={16} />
          <span className="sr-only">Change avatar</span>
        </label>
      )}
      <input 
        id={`avatar-upload-${userId}`}
        type="file" 
        accept="image/*" 
        className="hidden" 
        onChange={handleFileSelect}
        ref={fileInputRef}
        disabled={isUploading}
      />
    </div>
  );
};

export default SimpleAvatarUpload;